import * as React from "react";
import { cn } from "../lib/utils";

export interface PageHeaderAction {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  variant?: "primary" | "secondary" | "ghost" | "default";
  disabled?: boolean;
}

export interface PageHeaderProps {
  /** Page title — typically the page or module name. */
  title: string;
  /**
   * Optional subtitle / description for context.
   *
   * `ReactNode`, not `string`: it renders into a `<p>`, and callers legitimately
   * pass a fragment with a link or an emphasised span. `WizardShell` already
   * typed its own subtitle this way, so keeping this narrower only forced a cast
   * at the one place the two met.
   */
  subtitle?: React.ReactNode;
  /** Optional icon shown to the left of the title (Lucide component). */
  icon?: React.ComponentType<{ className?: string }>;
  /** Optional breadcrumb segment list (rendered above the title). */
  breadcrumbs?: { label: string; href?: string }[];
  /** Optional metadata pills (status, count, last updated). */
  meta?: React.ReactNode;
  /** Action buttons rendered on the right side of the header. */
  actions?: PageHeaderAction[];
  className?: string;
}

/**
 * Consistent page header for every Tyndo view. Mirrors Odoo's toolbar pattern:
 * breadcrumb → title row with actions on the right → optional meta row.
 */
export function PageHeader({
  title,
  subtitle,
  icon: Icon,
  breadcrumbs,
  meta,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <div className={cn("border-b bg-background pb-4", className)}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="mb-2 flex items-center gap-1 text-xs text-muted-foreground">
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={`${crumb.label}-${i}`}>
              {i > 0 && <span className="mx-1 text-muted-foreground/50">/</span>}
              {crumb.href ? (
                <a href={crumb.href} className="hover:text-foreground">
                  {crumb.label}
                </a>
              ) : (
                <span>{crumb.label}</span>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          {Icon && (
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted">
              <Icon className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="truncate font-heading text-xl font-semibold tracking-tight text-foreground">
              {title}
            </h1>
            {subtitle && (
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>
        </div>

        {actions && actions.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {actions.map((action) => (
              <button
                key={action.label}
                onClick={action.onClick}
                disabled={action.disabled}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none",
                  (action.variant === "primary" || action.variant === "default") &&
                    "bg-primary text-primary-foreground hover:bg-primary/90",
                  action.variant === "secondary" &&
                    "border bg-background hover:bg-muted",
                  (!action.variant || action.variant === "ghost") &&
                    "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                {action.icon ? <action.icon className="h-4 w-4" /> : null}
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {meta && (
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {meta}
        </div>
      )}
    </div>
  );
}
